import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "../../../components/DashboardLayout";
import Loader from "../../../components/Loader";
import { showErrorToastMsg } from "../../../utility/ToastMessage";
import {
  getCoursesAPI,
  getMyAssignedCoursesAPI,
} from "../../../services/portal/student/studentCourseService";
import { RoutePaths } from "../../../utility/RoutePaths";

const CourseCard = ({ course, isPaid, onJoin }) => (
  <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md hover:-translate-y-0.5 transition-all duration-200">
    {course.Image ? (
      <img
        src={course.Image}
        alt={course.Title}
        className="w-full h-40 object-cover"
      />
    ) : (
      <div className="w-full h-40 bg-primary-50 flex items-center justify-center">
        <svg
          className="w-12 h-12 text-primary-300"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
          />
        </svg>
      </div>
    )}

    <div className="p-4">
      <div className="flex items-start justify-between gap-2 mb-2">
        <h3 className="text-sm font-bold text-gray-900 line-clamp-2">
          {course.Title}
        </h3>
        {isPaid && (
          <span className="flex-shrink-0 inline-flex items-center px-2 py-0.5 rounded-full bg-green-50 text-green-700 text-xs font-medium">
            Paid
          </span>
        )}
      </div>

      {course.Description && (
        <p className="text-xs text-gray-500 line-clamp-2 mb-3">
          {course.Description}
        </p>
      )}

      <div className="flex items-center justify-between text-xs text-gray-500 mt-auto">
        {course.Date && (
          <div className="flex items-center gap-1">
            <svg
              className="w-3.5 h-3.5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            {new Date(course.Date).toLocaleDateString()}
          </div>
        )}
        {course.CourseFee !== undefined && course.CourseFee !== null && (
          <div className="flex items-center gap-1 font-semibold text-primary-600">
            <p className="text-primary-600">
              Rs. {course.CourseFee.toLocaleString()}
            </p>
          </div>
        )}
      </div>

      <div className="mt-4">
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => onJoin(course)}
            className="w-auto px-5 py-2.5 rounded-lg font-semibold text-sm transition-colors bg-primary-600 text-white hover:bg-primary-700"
          >
            {isPaid ? "View Details" : "Join"}
          </button>
        </div>
      </div>
    </div>
  </div>
);

const EmptyState = ({ message }) => (
  <div className="text-center py-12">
    <svg
      className="mx-auto w-14 h-14 text-gray-300 mb-3"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
      />
    </svg>
    <p className="text-gray-500 font-medium">{message}</p>
  </div>
);

const CoursesPage = () => {
  const navigate = useNavigate();
  const [loadingAll, setLoadingAll] = useState(false);
  const [loadingPaid, setLoadingPaid] = useState(false);
  const [allCourses, setAllCourses] = useState([]);
  const [paidCourses, setPaidCourses] = useState([]);

  useEffect(() => {
    loadAllCourses();
    loadPaidCourses();
  }, []);

  const loadAllCourses = async () => {
    setLoadingAll(true);
    try {
      const res = await getCoursesAPI();
      if (res && res.IsSuccessful && res.Data) {
        setAllCourses(res.Data);
      }
    } catch (error) {
      console.error("Error loading courses:", error);
      showErrorToastMsg("Failed to load courses");
    } finally {
      setLoadingAll(false);
    }
  };

  const loadPaidCourses = async () => {
    setLoadingPaid(true);
    try {
      const res = await getMyAssignedCoursesAPI();
      if (res && res.IsSuccessful && res.Data) {
        setPaidCourses(res.Data);
      }
    } catch (error) {
      console.error("Error loading assigned courses:", error);
      showErrorToastMsg("Failed to load your courses");
    } finally {
      setLoadingPaid(false);
    }
  };

  const handleJoin = (course) => {
    navigate(
      `${RoutePaths.STUDENT_COURSE_DETAILS_PAGE}?courseId=${course.CourseId}`,
    );
  };

  return (
    <DashboardLayout title="Courses" subtitle="Browse and view your courses">
      {/* My Courses */}
      <div className="mb-10">
        <h2 className="text-base font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <span className="w-1 h-5 bg-primary-600 rounded-full inline-block" />
          My Paid Courses
        </h2>

        {loadingPaid ? (
          <div className="flex justify-center py-10">
            <Loader />
          </div>
        ) : paidCourses.length === 0 ? (
          <EmptyState message="You have not paid for any courses yet." />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {paidCourses.map((course) => (
              <CourseCard
                key={course.Id}
                course={course}
                isPaid={true}
                onJoin={handleJoin}
              />
            ))}
          </div>
        )}
      </div>

      {/* All Courses */}
      <div>
        <h2 className="text-base font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <span className="w-1 h-5 bg-gray-400 rounded-full inline-block" />
          All Courses
        </h2>

        {loadingAll ? (
          <div className="flex justify-center py-10">
            <Loader />
          </div>
        ) : allCourses.length === 0 ? (
          <EmptyState message="No courses available at the moment." />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {allCourses.map((course) => (
              <CourseCard
                key={course.Id}
                course={course}
                isPaid={false}
                onJoin={handleJoin}
              />
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default CoursesPage;
